// src/pages/admin/AdminSecretos.js
// Gestión de secretos cifrados con Cloud KMS.
// El valor plano solo viaja al guardar (POST). Nunca se muestra
// en la tabla — solo metadatos (código, descripción, versión KMS,
// fecha modificación).

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Stack, Alert, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, TextField, Tooltip, Chip, Divider,
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import EditIcon   from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyIcon    from '@mui/icons-material/Key';
import { admin }  from '../../api/authApi';
import { fechaHoraLocal } from '../../utils/format';

// Códigos sugeridos para autocompletar. Deben calzar EXACTO con lo que
// WompiService lee (Tracker.Api/Services/HolaVeci/Pagos/IWompiService.cs):
// prefijo WOMPI_SANDBOX_ / WOMPI_PROD_ según Wompi:Ambiente en appsettings.
const CODIGOS_CONOCIDOS = [
  { codigo: 'WOMPI_SANDBOX_PUBLIC_KEY',    descripcion: 'Wompi SANDBOX - llave pública' },
  { codigo: 'WOMPI_SANDBOX_PRIVATE_KEY',   descripcion: 'Wompi SANDBOX - llave privada (cobros server-to-server)' },
  { codigo: 'WOMPI_SANDBOX_EVENTS_KEY',    descripcion: 'Wompi SANDBOX - llave para verificar firma del webhook' },
  { codigo: 'WOMPI_SANDBOX_INTEGRITY_KEY', descripcion: 'Wompi SANDBOX - llave para firmar integridad de la transacción' },
  { codigo: 'WOMPI_PROD_PUBLIC_KEY',       descripcion: 'Wompi PRODUCCIÓN - llave pública' },
  { codigo: 'WOMPI_PROD_PRIVATE_KEY',      descripcion: 'Wompi PRODUCCIÓN - llave privada (cobros server-to-server)' },
  { codigo: 'WOMPI_PROD_EVENTS_KEY',       descripcion: 'Wompi PRODUCCIÓN - llave para verificar firma del webhook' },
  { codigo: 'WOMPI_PROD_INTEGRITY_KEY',    descripcion: 'Wompi PRODUCCIÓN - llave para firmar integridad de la transacción' },
];

export default function AdminSecretos() {
  const [items, setItems]       = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError]       = useState('');
  const [aviso, setAviso]       = useState('');

  const [dialog, setDialog] = useState(null); // { modo, codigo, valor, descripcion }
  const [borrar, setBorrar] = useState(null); // { codigo }
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const r = await admin.secretosListar();
      setItems(Array.isArray(r) ? r : []);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudieron cargar los secretos.');
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = (plantilla) => {
    setDialog({
      modo: 'crear',
      codigo:      plantilla?.codigo      || '',
      valor:       '',
      descripcion: plantilla?.descripcion || '',
    });
  };

  const abrirEditar = (item) => {
    setDialog({
      modo: 'editar',
      codigo:      item.codigo,
      valor:       '',
      descripcion: item.descripcion || '',
    });
  };

  const guardar = async () => {
    if (!dialog?.codigo?.trim())  { setError('Código requerido.'); return; }
    if (!dialog?.valor)           { setError('Valor requerido.'); return; }

    setGuardando(true); setError('');
    try {
      await admin.secretosGuardar({
        codigo:      dialog.codigo.trim(),
        valor:       dialog.valor,
        descripcion: dialog.descripcion || null,
      });
      setAviso(`Secreto '${dialog.codigo.trim().toUpperCase()}' guardado. El valor plano no queda en pantalla ni en el servidor.`);
      setDialog(null);
      cargar();
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo guardar el secreto.');
    } finally { setGuardando(false); }
  };

  const confirmarEliminar = async () => {
    setGuardando(true); setError('');
    try {
      await admin.secretosEliminar(borrar.codigo);
      setAviso(`Secreto '${borrar.codigo}' eliminado.`);
      setBorrar(null);
      cargar();
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo eliminar.');
    } finally { setGuardando(false); }
  };

  const fmtFecha = (f) =>
    f ? fechaHoraLocal(f) : '—';

  return (
    <Box>
      <Typography variant="h4" sx={{ fontFamily: 'Fraunces', mb: 3 }}>Secretos</Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>Ojo.</strong> Los valores que registres acá se cifran con Cloud KMS antes de guardarse.
        El valor plano viaja solo en el momento del POST — asegúrate de estar en HTTPS y en una red confiable.
        Después de guardar, el valor no se puede leer desde la interfaz.
      </Alert>

      {aviso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso('')}>{aviso}</Alert>}
      {error && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Códigos sugeridos rápidos */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Registro rápido de llaves Wompi
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {CODIGOS_CONOCIDOS.map(p => {
              const yaExiste = items.some(i => i.codigo === p.codigo);
              return (
                <Chip
                  key={p.codigo}
                  label={p.codigo}
                  icon={<KeyIcon />}
                  color={yaExiste ? 'success' : 'default'}
                  variant={yaExiste ? 'filled' : 'outlined'}
                  onClick={() => abrirNuevo(p)}
                  clickable
                />
              );
            })}
          </Stack>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
            Verde = ya registrado. Clic para actualizar o crear.
          </Typography>
        </CardContent>
      </Card>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Registrados</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirNuevo()}>
          Nuevo secreto
        </Button>
      </Stack>

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>KMS Key Version</TableCell>
                <TableCell>Última modificación</TableCell>
                <TableCell>Modificado por</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Aún no hay secretos registrados.
                  </TableCell>
                </TableRow>
              ) : items.map(it => (
                <TableRow key={it.codigo}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {it.codigo}
                    </Typography>
                  </TableCell>
                  <TableCell>{it.descripcion || '—'}</TableCell>
                  <TableCell>
                    <Tooltip title={it.kmsKeyVersion || 'sin versión registrada'}>
                      <Typography variant="caption" sx={{
                        fontFamily: 'monospace',
                        display: 'block',
                        maxWidth: 220,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {it.kmsKeyVersion?.split('/').slice(-2).join('/') || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{fmtFecha(it.fechaModificacion)}</TableCell>
                  <TableCell>{it.modificadoPor || '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Actualizar valor">
                      <IconButton size="small" onClick={() => abrirEditar(it)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => setBorrar(it)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Dialog crear/actualizar */}
      <Dialog open={!!dialog} onClose={() => !guardando && setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {dialog?.modo === 'editar' ? 'Actualizar valor' : 'Nuevo secreto'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Código"
              value={dialog?.codigo || ''}
              onChange={(e) => setDialog(d => ({ ...d, codigo: e.target.value }))}
              disabled={dialog?.modo === 'editar'}
              helperText="Se normaliza a MAYÚSCULAS. Ej. WOMPI_PRIVATE_KEY"
              fullWidth
            />
            <TextField
              label="Valor"
              type="password"
              value={dialog?.valor || ''}
              onChange={(e) => setDialog(d => ({ ...d, valor: e.target.value }))}
              helperText="El valor viaja una única vez. Se cifra con KMS antes de guardarse."
              fullWidth
              autoComplete="new-password"
            />
            <TextField
              label="Descripción (opcional)"
              value={dialog?.descripcion || ''}
              onChange={(e) => setDialog(d => ({ ...d, descripcion: e.target.value }))}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={guardando}>Cancelar</Button>
          <Button onClick={guardar} variant="contained" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar cifrado'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar eliminar */}
      <Dialog open={!!borrar} onClose={() => !guardando && setBorrar(null)}>
        <DialogTitle>Eliminar secreto</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Seguro que quieres eliminar <strong>{borrar?.codigo}</strong>?
            Los servicios que dependan de este secreto dejarán de funcionar hasta que lo vuelvas a registrar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBorrar(null)} disabled={guardando}>Cancelar</Button>
          <Button onClick={confirmarEliminar} color="error" variant="contained" disabled={guardando}>
            {guardando ? 'Eliminando…' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
